import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Tabs, TabList, Tab } from 'aquilla/core/molecules/Tabs';
import pushRoute from '@utils/pushRoute';
import { mediaQuery, Spacings } from '@styles/index';
import {
  trackUpscAnalysisCtaClicked,
  trackUpscCalendarCtaClicked,
  trackUpscDailyPdfCtaClicked,
  trackUpscMonthlyClassesCtaClicked,
  trackUpscMonthlyPdfCtaClicked,
  trackUpscQuizCtaClicked,
  trackUpscSubjectWiseCtaClicked
} from '@cont/UpscCurrentAffairs/upscCurrentAffairsAnalytics';
import {
  getUpscNavTabIndexFromPathname,
  UPSC_MAIN_HEADER_OFFSET,
  UPSC_NAV_TABS,
  UPSC_SUB_NAV_HEIGHT
} from './upscCurrentAffairsHeaderUtils';

const SubNavContainer = styled.nav`
  width: 100%;
  min-height: ${UPSC_SUB_NAV_HEIGHT.desktop};
  background: var(--color-base-1);
  position: sticky;
  top: ${UPSC_MAIN_HEADER_OFFSET.desktop};
  z-index: 2;
  border-bottom: ${Spacings.SPACING_1} solid #e9eef2;
  ${mediaQuery.uptoMobile} {
    min-height: ${UPSC_SUB_NAV_HEIGHT.mobile};
    top: ${UPSC_MAIN_HEADER_OFFSET.mobile};
  }
`;

const TabSubContainer = styled.div`
  width: 1136px;
  max-width: 100%;
  margin: 0 auto;
  .tablist {
    min-height: 32px;
  }
  .aquilla-tab {
    min-height: 32px;
    height: 32px;
  }
  ${mediaQuery.uptoMobile} {
    .tablist {
      min-height: 40px;
    }
    .aquilla-tab {
      min-height: 40px;
      height: 40px;
    }
  }
`;

const TabWrapper = styled.div`
  height: 100%;
  .aquilla-tablist {
    justify-content: flex-start;
  }
  ${mediaQuery.uptoTablet} {
    padding: 0 ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_4B};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  > * {
    height: 100%;
  }
`;

const TabLabel = styled.span`
  display: flex;
  align-items: center;
`;

const trackUpscNavTabClick = {
  upsc_nav_todays_analysis: trackUpscAnalysisCtaClicked,
  upsc_nav_quiz: trackUpscQuizCtaClicked,
  upsc_nav_calendar: trackUpscCalendarCtaClicked,
  upsc_nav_daily_pdf: trackUpscDailyPdfCtaClicked,
  upsc_nav_monthly_pdf: (ctaText) => trackUpscMonthlyPdfCtaClicked({ ctaText }),
  upsc_nav_monthly_classes: trackUpscMonthlyClassesCtaClicked,
  upsc_nav_subject_wise: (ctaText) =>
    trackUpscSubjectWiseCtaClicked({ ctaText })
};

const UpscCurrentAffairsSubNav = () => {
  const { pathname } = useRouter();
  const [currentTabIndex, setCurrentTabIndex] = useState(() =>
    getUpscNavTabIndexFromPathname(pathname)
  );

  useEffect(() => {
    setCurrentTabIndex(getUpscNavTabIndexFromPathname(pathname));
  }, [pathname]);

  const handleOnChange = (index) => {
    const tab = UPSC_NAV_TABS[index];
    trackUpscNavTabClick[tab?.id]?.(tab.label);
    if (!tab?.path || tab.path === pathname) {
      setCurrentTabIndex(index);
      return;
    }
    setCurrentTabIndex(index);
    pushRoute(tab.path, tab.path);
  };

  return (
    <SubNavContainer aria-label="UPSC Current Affairs navigation">
      <TabSubContainer>
        <TabWrapper>
          <Tabs onChange={handleOnChange} index={currentTabIndex}>
            <TabList>
              {UPSC_NAV_TABS.map((tab) => (
                <Tab key={tab.id} id={tab.id}>
                  <TabLabel>{tab.label}</TabLabel>
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </TabWrapper>
      </TabSubContainer>
    </SubNavContainer>
  );
};

export default UpscCurrentAffairsSubNav;
